import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, ExternalLink, X as XIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import { getPendingCompanions, reviewCompanion } from '../api/services';
import {
  formatDate,
  getApiErrorMessage,
  getDisplayName,
  slugToLabel,
} from '../utils/admin';

const Companions = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [reviewState, setReviewState] = useState({
    isOpen: false,
    decision: '',
    profile: null,
  });

  const fetchProfiles = useCallback(async (page, limit = pagination.limit) => {
    setLoading(true);

    try {
      const response = await getPendingCompanions({ page, limit });
      setProfiles(response.data || []);
      setPagination({
        page: response.pagination?.page || page,
        limit: response.pagination?.limit || limit,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 1,
      });
    } catch (error) {
      console.error('Failed to load companion applications', error);
      toast.error(getApiErrorMessage(error, 'Failed to load companion applications'));
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchProfiles(pagination.page, pagination.limit);
  }, [fetchProfiles, pagination.limit, pagination.page]);

  const openReviewModal = (profile, decision) => {
    setReviewState({
      isOpen: true,
      decision,
      profile,
    });
  };

  const closeReviewModal = () => {
    setReviewState({
      isOpen: false,
      decision: '',
      profile: null,
    });
  };

  const handleReview = async () => {
    if (!reviewState.profile || !reviewState.decision) {
      return;
    }

    try {
      const response = await reviewCompanion(reviewState.profile._id, reviewState.decision);
      toast.success(response.message || `Application ${reviewState.decision}`);
      closeReviewModal();
      fetchProfiles(pagination.page);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to review companion application'));
    }
  };

  const handlePageChange = (page) => {
    setPagination((previous) => ({
      ...previous,
      page,
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Companion Applications</h1>
        <p className="mt-1 text-sm text-gray-500">Review pending companion profiles from the new admin API.</p>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 text-sm text-gray-500">
          {pagination.total > 0
            ? `Showing ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
                pagination.page * pagination.limit,
                pagination.total
              )} of ${pagination.total} pending applications`
            : 'No pending applications'}
        </div>

        {loading ? (
          <div className="flex justify-center px-6 py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
          </div>
        ) : profiles.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No pending companion applications found.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {profiles.map((profile) => (
              <div key={profile._id} className="grid gap-4 px-6 py-5 lg:grid-cols-[1.6fr_1fr_auto]">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {getDisplayName(profile.userDetails, `Profile ${profile._id.slice(-6)}`)}
                    </h2>
                    <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                      {slugToLabel(profile.status)}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600">{profile.bio || 'No bio provided.'}</p>

                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="rounded-full bg-gray-100 px-3 py-1">City: {profile.city || 'N/A'}</span>
                    <span className="rounded-full bg-gray-100 px-3 py-1">Rate: INR {profile.hourlyRate ?? 'N/A'}</span>
                    <span className="rounded-full bg-gray-100 px-3 py-1">Available: {profile.isAvailable ? 'Yes' : 'No'}</span>
                    <span className="rounded-full bg-gray-100 px-3 py-1">Gallery: {profile.gallery?.length || 0}</span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p>Categories: {profile.activityCategories?.length ? profile.activityCategories.join(', ') : 'N/A'}</p>
                    <p>Languages: {profile.languages?.length ? profile.languages.join(', ') : 'N/A'}</p>
                    <p>Submitted: {formatDate(profile.createdAt)}</p>
                  </div>
                </div>

                <div className="space-y-3 rounded-lg bg-gray-50 p-4 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">User ID</p>
                    <p className="mt-1 break-all text-gray-800">{profile.userId}</p>
                  </div>

                  <Link
                    to={`/users/${profile.userId}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink size={16} className="mr-1" /> Open user profile
                  </Link>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => openReviewModal(profile, 'approved')}
                    className="inline-flex items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                  >
                    <Check size={16} className="mr-2" /> Approve
                  </button>
                  <button
                    onClick={() => openReviewModal(profile, 'rejected')}
                    className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    <XIcon size={16} className="mr-2" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && pagination.pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} className="mr-2" /> Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next <ChevronRight size={16} className="ml-2" />
            </button>
          </div>
        )}
      </div>

      <Modal
        isOpen={reviewState.isOpen}
        onClose={closeReviewModal}
        title={`${slugToLabel(reviewState.decision)} Companion Application`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Confirm that you want to {reviewState.decision} this companion application.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={closeReviewModal}
              className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleReview}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
                reviewState.decision === 'approved'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Companions;
